import React, { useState } from 'react';
import {
  requestPasswordResetApi,
  verifyPasswordResetOtpApi,
  confirmNewPasswordApi
} from '../apis/payments.api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

/**
 * Password Reset Component
 * 
 * Three-step password reset flow:
 * 1. Enter email to receive OTP
 * 2. Verify OTP code
 * 3. Set new password
 */
const PasswordResetFlow = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Step 1: Request password reset
  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.warning('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await requestPasswordResetApi({ email });
      toast.success('📧 Verification code sent to your email');
      setStep(2);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to send verification code';
      if (errorMsg === 'User not found') {
        toast.error('❌ No account found with this email address');
      } else {
        toast.error(`❌ ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast.warning('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    try {
      await verifyPasswordResetOtpApi({ email, otp });
      toast.success('✅ Code verified successfully');
      setStep(3);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Invalid code';
      if (errorMsg === 'Code expired') {
        toast.error('❌ Code has expired. Please request a new one.');
      } else {
        toast.error('❌ Invalid verification code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Set new password
  const handleConfirmPassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newPassword || !confirmPassword) {
      toast.warning('Please fill in all fields');
      return;
    }

    if (newPassword.length < 8) {
      toast.warning('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await confirmNewPasswordApi({ email, new_password: newPassword });
      toast.success('✅ Password reset successful! Please login with your new password.');
      
      // Redirect to login page after 2 seconds
      setTimeout(() => {
        navigate('/signin');
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to reset password';
      if (errorMsg === 'OTP expired') {
        toast.error('❌ Verification expired. Please start over.');
        setStep(1);
      } else {
        toast.error(`❌ ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await requestPasswordResetApi({ email });
      toast.success('📧 New verification code sent');
    } catch (error) {
      toast.error('Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      {/* ===== BACKGROUND CIRCLES ===== */}
<div className="absolute -top-24 -left-24 w-72 h-72 bg-[#008300]/10 rounded-full"></div>
<div className="absolute top-40 right-0 w-64 h-64 bg-[#0B1B3F]/10 rounded-full"></div>
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">Reset Password</h2>
          <p className="mt-2 text-sm text-gray-600">
            {step === 1 && 'Enter your email to receive a verification code'}
            {step === 2 && 'Enter the verification code sent to your email'}
            {step === 3 && 'Create your new password'}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center items-center space-x-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div
                  className={`w-12 h-1 ${
                    step > s ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Email Input */}
        {step === 1 && (
          <form onSubmit={handleRequestReset} className="mt-8 space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-white font-medium ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Sending...
                </span>
              ) : (
                'Send Verification Code'
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/signin')}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                Back to Sign In
              </button>
            </div>
          </form>
        )}

        {/* Step 2: OTP Verification */}
        {step === 2 && (
          <form onSubmit={handleVerifyOtp} className="mt-8 space-y-6">
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg text-center text-2xl tracking-widest font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="000000"
              />
              <p className="mt-2 text-sm text-gray-500">
                Code sent to: <span className="font-semibold">{email}</span>
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-white font-medium ${
                loading || otp.length !== 6
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>

            <div className="flex justify-between text-sm">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-gray-600 hover:text-gray-500"
              >
                ← Change Email
              </button>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={loading}
                className="text-blue-600 hover:text-blue-500"
              >
                Resend Code
              </button>
            </div>
          </form>
        )}

        {/* Step 3: New Password */}
        {step === 3 && (
          <form onSubmit={handleConfirmPassword} className="mt-8 space-y-6">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Must be at least 8 characters long
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Confirm new password"
              />
            </div>

            {/* Password strength indicator */}
            {newPassword && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-xs">
                  <span
                    className={`w-3 h-3 rounded-full ${
                      newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  />
                  <span>At least 8 characters</span>
                </div>
                <div className="flex items-center space-x-2 text-xs">
                  <span
                    className={`w-3 h-3 rounded-full ${
                      /[A-Z]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  />
                  <span>Contains uppercase letter</span>
                </div>
                <div className="flex items-center space-x-2 text-xs">
                  <span
                    className={`w-3 h-3 rounded-full ${
                      /[0-9]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  />
                  <span>Contains number</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-white font-medium ${
                loading || !newPassword || !confirmPassword
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
              }`}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PasswordResetFlow;
